<template>
  <div class="group-listing species-table">
    <div class="mb-12">
      <v-row class="flex-column flex-md-row">
        <v-col>
          <v-card-title
            class="display-2 letter-spacing-normal font-weight-bold mb-md-12 align-center px-5"
          >
            Your Species Data
          </v-card-title>
        </v-col>
        <v-col>
          <v-text-field
            class="px-5 mt-0 mt-md-1 pt-0 pt-md-3"
            append-icon="$search"
            label="Search"
            v-model="search"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-data-table
            :fixed-header="false"
            class="primary-table border-radius-0 v-data-table-1 v-data-table-1--style1"
            :headers="headers"
            :items="species"
            :loading="model.fetching"
            loading-text="fetching data..."
            no-data-text="No data"
            no-results-text="Unable to find any data"
            :options.sync="pagination"
            :server-items-length="total"
          >
            <template v-slot:item.image="{ item }">
              <v-avatar tile class="pa-0" width="106" height="72">
                <v-img
                  :src="getMedia(item)"
                  lazy-src="/placeholder.jpg"
                  :alt="item.name"
                />
              </v-avatar>
            </template>
            <template v-slot:item.cost="{ item, value }">
              <div style="width: 150px;">
                ${{ value }}
                <a
                  v-if="currentUser.isTourismOperator()"
                  href="javascript:"
                  @click="showStatisticsWithCustomCost(item)"
                  class="primary--text pl-2"
                >
                  Change Cost
                </a>
              </div>
            </template>
            <template v-slot:item.statistics="{ item }">
              <a
                href="javascript:"
                @click="showStatistics(item)"
                class="primary--text"
              >
                Full Statistics
              </a>
            </template>
            <template v-slot:item.health="{ value }">
              {{ value || '--' }}
            </template>
            <template v-slot:item.action="{ item }">
              <v-icon @click="edit(item)">$edit</v-icon>
              <v-icon @click="remove(item)">$delete</v-icon>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <v-row v-if="currentUser.can('edit', 'species')">
        <v-col>
          <v-card-actions class="mt-10 px-5">
            <v-btn
              depressed
              large
              color="black"
              class="white--text px-14"
              @click="newSpecies()"
            >
              New Species
              <v-icon class="ml-10" right @click="createMode = true">
                $plus
              </v-icon>
            </v-btn>
          </v-card-actions>
        </v-col>
      </v-row>
    </div>
    <Drawer
      @close="displayStatistics = false"
      v-model="displayStatistics"
      width="600"
      v-if="selectedSpecies"
    >
      <SpeciesStatistics
        :species="selectedSpecies"
        @speciesCostChange="onSpeciesCostChange"
        :showCustomCost="showCustomCost"
      />
      <template v-slot:append>
        <div
          class="px-5 px-md-5 px-md-24 py-12 d-flex flex-column flex-md-row justify-lg-space-between"
        >
          <v-btn min-width="45%" large class="white px-14 mb-3 mb-md-0">
            Get Help
          </v-btn>
          <v-btn
            min-width="45%"
            @click="updateCustomPrice"
            large
            class="primary px-14"
          >
            Done
          </v-btn>
        </div>
      </template>
    </Drawer>
  </div>
</template>

<script>
import Model from '@models/Species';

export default {
  name: 'Species',
  metaInfo: {
    titleTemplate: `%s - Species`,
  },
  components: {
    Drawer: () => import('@layouts/_partials/Drawer'),
    SpeciesStatistics: () => import('./_paritials/SpeciesStatistics'),
  },
  data() {
    return {
      showCustomCost: false,
      speciesCustomCost: {},
      createMode: false,
      disableSearchWatch: false,
      headers: [
        {
          text: '',
          value: 'image',
          sortable: false,
        },
        {
          text: 'Name',
          value: 'name',
          width: 248,
        },
        {
          text: 'Family',
          value: 'family.name',
          sortable: false,
        },
        {
          text: 'Latin Name',
          value: 'latin_name',
          width: 248,
        },
        {
          text: 'Sighting Chance',
          value: 'sighting_chance',
          sortable: false,
        },
        {
          text: 'Health',
          value: 'health_text',
          sortable: false,
        },
        {
          text: 'Cost',
          width: 200,
          value: 'cost',
        },
        {
          text: 'Statistics',
          value: 'statistics',
          width: 150,
          sortable: false,
        },
      ].concat(
        this.$store.state.auth.user.can('edit', 'species')
          ? [
              {
                text: 'Action',
                value: 'action',
                width: 150,
                sortable: false,
              },
            ]
          : []
      ),
      pagination: {
        sortBy: ['created_at'],
        itemsPerPage: 10,
        page: 1,
        sortDesc: [true],
      },
      search: '',
      species: [],
      model: new Model({
        relations: ['family'],
        filters: {
          user_id: this.$store.state.auth.user.isTourismOperator()
            ? this.$store.state.auth.user.id
            : '',
          id: this.$route.query.species_id || '',
        },
      }),
      selectedSpecies: {},
      displayStatistics: false,
      routeData: {},
      total: 10,
    };
  },
  watch: {
    pagination: {
      handler() {
        Object.assign(this.model, this.pagination);
        this.isMounted && this.fetchData();
      },
      deep: true,
      immediate: false,
    },
    search() {
      if (this.disableSearchWatch) {
        this.disableSearchWatch = false;
        return;
      }
      this.fetchData();
    },
  },
  methods: {
    async updateCustomPrice() {
      if (!this.showCustomCost) {
        this.displayStatistics = false;
        return;
      }

      const cost = Number(this.speciesCustomCost[this.selectedSpecies.id]);

      if (isNaN(cost)) {
        $showError('Invalid Cost Supplied', 'invalid-cost');
        return;
      }

      await this.model.updateCustomPrice(this.selectedSpecies.id, cost);
      const speciesIndex = this.species.findIndex(
        (species) => species.id === this.selectedSpecies.id
      );

      this.selectedSpecies.cost = cost;
      this.selectedSpecies.has_custom_cost = true;
      this.species[speciesIndex] = this.selectedSpecies;
      this.displayStatistics = false;
    },
    onSpeciesCostChange(value) {
      this.speciesCustomCost = value;
    },
    async fetchData() {
      this.syncPagination();
      let firstFetching = !this.model.fetched;
      this.loading = true;
      let response = await this.model.get();
      firstFetching && this.model.filters.id && this.model.removeFilter('id');
      if (response) {
        if (firstFetching && this.model.filters.id) {
          this.disableSearchWatch = true;
          this.search = response.data[0].name;
        }
        this.species = response.data;
        this.total = response.pagination.total;
        let action = this.$route.query.action || '';
        if (action) {
          switch (action) {
            case 'stats':
              this.showStatistics(this.species[0]);
              return;
            default:
              return;
          }
        }
      }
      this.loading = false;
    },
    syncPagination() {
      Object.assign(this.model, this.pagination);
      (this.search &&
        this.model.setFilters({
          name: this.search,
        })) ||
        this.model.removeFilter('name');
    },
    async edit(species) {
      this.$router.push({
        name: 'edit-species',
        params: {
          id: species.id,
        },
      });
    },
    async remove(species) {
      if (
        !(await $question('Are you sure, you want to delete this species?'))
      ) {
        return;
      }
      if (await this.model.delete(species.id)) {
        $showSuccess('Species has been removed.', 'delete-species');
        this.fetchData();
      }
    },
    showStatisticsWithCustomCost(species) {
      this.showCustomCost = true;
      this.selectedSpecies = species;
      this.displayStatistics = true;
    },
    showStatistics(species) {
      this.showCustomCost = false;
      this.selectedSpecies = species;
      this.displayStatistics = true;
    },
    newSpecies() {
      this.$router.push({
        name: 'new-species',
      });
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
